import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import { Marker, useMapEvents } from "react-leaflet";
import MapComponent from "../components/MapCompnent";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import api from "../services/httpClient";
import PostcodeInput, {
  isValidPostcode,
} from "../components/Common/PostcodeInput";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "../assets/styles/newAddress.scss";

// Fix domyslnej ikony Leaflet w React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Slowniki (stale — identyczne z backendem NewAddressConstants)
const provinces = [
  "",
  "wielkopolskie",
  "kujawsko-pomorskie",
  "małopolskie",
  "łódzkie",
  "dolnośląskie",
  "lubelskie",
  "lubuskie",
  "mazowieckie",
  "opolskie",
  "podlaskie",
  "pomorskie",
  "śląskie",
  "podkarpackie",
  "świętokrzyskie",
  "warmińsko-mazurskie",
  "zachodniopomorskie",
];
const attributes = ["", "ul.", "rynek", "al.", "pl."];
const buildingTypes = ["", "B", "C"];
const postcodeTypes = ["", "bazowy", "wirtualny"];

// Komponent nasłuchujący kliknięć na mapę
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
};

const NewAddressForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    province: "",
    cityName: "",
    attribute: "",
    street: "",
    number: "",
    postcode: "",
    x: 0,
    y: 0,
    type: "",
    building: "",
  });

  const [markerPosition, setMarkerPosition] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const markerRef = useRef(null);

  // Pobranie danych adresu przy edycji
  useEffect(() => {
    if (!isEditMode) return;
    const fetchAddress = async () => {
      try {
        const response = await api.get(`/NewAddress/${id}`);
        const result = response.data?.data;
        if (result?.rows && result.rows.length > 0) {
          const addr = result.rows[0];
          setFormData({
            province: addr.province || "",
            cityName: addr.cityName || "",
            attribute: addr.attribute || "",
            street: addr.street || "",
            number: addr.number || "",
            postcode: addr.postcode || "",
            x: addr.x || 0,
            y: addr.y || 0,
            type: addr.type || "",
            building: addr.building || "",
          });
          if (addr.y !== 0 && addr.x !== 0) {
            setMarkerPosition([addr.y, addr.x]);
          }
        }
      } catch (error) {
        console.error("Error fetching address for edit:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchAddress();
  }, [id, isEditMode]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Gdy ulica zostaje wyczyszczona — wyczyść też atrybut
      if (field === "street" && !value.trim()) {
        updated.attribute = "";
      }

      return updated;
    });
  };

  // Kliknięcie na mapę — ustawia marker i współrzędne x/y
  // Konwencja: x = longitude (lng), y = latitude (lat)
  const handleMapClick = (latlng) => {
    setMarkerPosition([latlng.lat, latlng.lng]);
    setFormData((prev) => ({
      ...prev,
      x: latlng.lng,
      y: latlng.lat,
    }));
  };

  // Przesuwanie markera (draggable)
  const handleMarkerDragEnd = () => {
    const marker = markerRef.current;
    if (marker) {
      const pos = marker.getLatLng();
      setMarkerPosition([pos.lat, pos.lng]);
      setFormData((prev) => ({
        ...prev,
        x: pos.lng,
        y: pos.lat,
      }));
    }
  };

  // Geolokalizacja — wyszukiwanie adresu przez OpenStreetMap Nominatim API
  const handleGeolocate = async () => {
    const query = [formData.cityName, formData.street, formData.number]
      .filter(Boolean)
      .join(" ");
    if (!query.trim()) {
      alert("Wprowadź miejscowość, ulicę lub numer do geolokalizacji.");
      return;
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=pl&limit=1`,
      );
      const results = await response.json();
      if (results.length > 0) {
        const lat = parseFloat(results[0].lat);
        const lng = parseFloat(results[0].lon);
        setMarkerPosition([lat, lng]);
        setFormData((prev) => ({
          ...prev,
          x: lng,
          y: lat,
        }));
      } else {
        alert("Nie znaleziono lokalizacji dla podanego adresu.");
      }
    } catch (error) {
      console.error("Geocoding error:", error);
      alert("Błąd geolokalizacji.");
    }
  };

  // Wysłanie formularza
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Walidacja: kod pocztowy wymagany i w prawidłowym formacie
    if (!formData.postcode || !formData.postcode.trim()) {
      alert("Kod pocztowy jest wymagany.");
      return;
    }
    if (!isValidPostcode(formData.postcode)) {
      alert(
        "Nieprawidłowy format kodu pocztowego. \nWymagany format: XX-XXX (np. 72-320).",
      );
      return;
    }

    // Walidacja: atrybut wymagany gdy ulica jest uzupełniona
    if (formData.street.trim() && !formData.attribute) {
      alert("Atrybut jest wymagany, gdy ulica jest uzupełniona.");
      return;
    }

    setSubmitting(true);

    try {
      const requestBody = {
        data: {
          province: formData.province,
          cityName: formData.cityName,
          attribute: formData.street.trim() ? formData.attribute : null,
          street: formData.street,
          number: formData.number,
          postcode: formData.postcode,
          x: formData.x,
          y: formData.y,
          type: formData.type,
          building: formData.building,
        },
      };

      if (isEditMode) {
        await api.patch(`/NewAddress/${id}`, requestBody);
        alert("Adres został zaktualizowany.");
        navigate(`/administration/new-addresses/${id}`);
      } else {
        await api.post(`/NewAddress`, requestBody);
        alert("Nowy adres został dodany.");
        navigate("/administration/new-addresses");
      }
    } catch (error) {
      if (error.response?.status === 423) {
        alert(
          "Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana",
        );
        return;
      }

      const msg =
        error.response?.data?.data?.message ||
        "Wystąpił błąd podczas zapisywania adresu.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="mt-4">
        <p>Ladowanie...</p>
      </Container>
    );
  }

  // Centrum mapy: istniejące współrzędne lub domyślne (Łódź)
  const mapCenter = markerPosition || [51.759, 19.458];

  return (
    <div className="new-address-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="New Address Icon"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              {isEditMode ? `Edycja adresu #${id}` : "Dodaj nowy adres"}
            </h3>
          </Col>
          <Col xs="auto">
            <Button
              variant="secondary"
              onClick={() => navigate("/administration/new-addresses")}
            >
              Powrot do listy
            </Button>
          </Col>
        </Row>

        <Row>
          {/* Lewa kolumna — formularz (4/12) */}
          <Col md={4}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2" controlId="province">
                <Form.Label>Województwo</Form.Label>
                <Form.Select
                  value={formData.province}
                  onChange={(e) => handleChange("province", e.target.value)}
                >
                  {provinces.map((p) => (
                    <option key={p} value={p}>
                      {p || "-- Wybierz --"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2" controlId="postcode">
                <Form.Label>
                  Kod pocztowy <span className="text-danger">*</span>
                </Form.Label>
                <PostcodeInput
                  value={formData.postcode}
                  onChange={(val) => handleChange("postcode", val)}
                  mode="form"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="type">
                <Form.Label>Typ kodu pocztowego</Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                >
                  {postcodeTypes.map((t) => (
                    <option key={t} value={t}>
                      {t || "-- Wybierz --"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2" controlId="cityName">
                <Form.Label>Miejscowość</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Miejscowość..."
                  value={formData.cityName}
                  onChange={(e) => handleChange("cityName", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="attribute">
                <Form.Label>
                  Atrybut{" "}
                  {formData.street.trim() && (
                    <span className="text-danger">*</span>
                  )}
                </Form.Label>
                <Form.Select
                  value={formData.attribute}
                  onChange={(e) => handleChange("attribute", e.target.value)}
                  disabled={!formData.street.trim()}
                  isInvalid={!!formData.street.trim() && !formData.attribute}
                >
                  {attributes.map((a) => (
                    <option key={a} value={a}>
                      {a || "-- Wybierz --"}
                    </option>
                  ))}
                </Form.Select>
                {!!formData.street.trim() && !formData.attribute && (
                  <Form.Control.Feedback type="invalid">
                    Atrybut jest wymagany, gdy ulica jest uzupełniona.
                  </Form.Control.Feedback>
                )}
              </Form.Group>

              <Form.Group className="mb-2" controlId="street">
                <Form.Label>Ulica</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ulica (opcjonalnie)..."
                  value={formData.street}
                  onChange={(e) => handleChange("street", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="number">
                <Form.Label>Numer</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Numer domu..."
                  value={formData.number}
                  onChange={(e) => handleChange("number", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="building">
                <Form.Label>Typ budynku</Form.Label>
                <Form.Select
                  value={formData.building}
                  onChange={(e) => handleChange("building", e.target.value)}
                >
                  {buildingTypes.map((b) => (
                    <option key={b} value={b}>
                      {b || "-- Wybierz --"}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>x (longitude): {formData.x.toFixed(6)}</Form.Label>
                <br />
                <Form.Label>y (latitude): {formData.y.toFixed(6)}</Form.Label>
              </Form.Group>

              <div className="d-flex gap-2 mt-3">
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting
                    ? "Zapisywanie..."
                    : isEditMode
                      ? "Zapisz zmiany"
                      : "Dodaj adres"}
                </Button>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={handleGeolocate}
                >
                  Geolokalizuj
                </Button>
              </div>
            </Form>
          </Col>

          {/* Prawa kolumna — mapa */}
          <Col md={8}>
            <MapComponent
              center={mapCenter}
              zoom={markerPosition ? 15 : 6}
              style={{ height: "600px", width: "100%" }}
            >
              <MapClickHandler onMapClick={handleMapClick} />
              {markerPosition && (
                <Marker
                  position={markerPosition}
                  draggable={true}
                  ref={markerRef}
                  eventHandlers={{ dragend: handleMarkerDragEnd }}
                />
              )}
            </MapComponent>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NewAddressForm;
